import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import axios from "axios";

import { useApp } from "../context/AppContext";
import StatCard from "../components/StatCard";
import { SERVICE_LOCATION, CLIENT_OVERVIEW_API } from "../constants/endpoints";

// ── Component ─────────────────────────────────────────────────
const Dashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading, showToast } = useApp();

    const { data } = location.state || {};

    const [clientOverview, setClientOverview] = useState([]);
    const [totalSites, setTotalSites] = useState(0);
    const [totalMeasures, setTotalMeasures] = useState(0);
    const [selectedSite, setSelectedSite] = useState(null);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        systemId: { value: null, matchMode: FilterMatchMode.CONTAINS },
        siteName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    // ── Fetch ─────────────────────────────────────────────────
    const getClientOverview = useCallback(() => {
        if (!data?.data?.clientDetails?.clientAccount) return;
        setLoading(true);
        axios
            .get(
                SERVICE_LOCATION + CLIENT_OVERVIEW_API,
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            )
            .then(res => {
                if (res.data.isSuccess) {
                    const list = res.data.data.getClientOverview;
                    setClientOverview(list);
                    setTotalSites(list.length);
                    setTotalMeasures(list.reduce((s, i) => s + i.totalMeasures, 0));
                } else {
                    showToast("error", res.data.errorMessage || "Failed to load overview.");
                }
            })
            .catch(() => {
                showToast("error", "Network error. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [data, setLoading, showToast]);

    useEffect(() => { getClientOverview(); }, [getClientOverview]);

    // ── Row select → navigate to Sites ────────────────────────
    const onSiteRowSelect = (event) => {
        data.siteInfo = event.data;
        navigate("/home/sites", { state: { data } });
    };

    // ── Disbale unsubmitted worksheets to prevent from download ────────────────────────────────
    const isSelectable = (data) => data.totalMeasures !== 0;
    const isRowSelectable = (event) => (event.data ? isSelectable(event.data) : true);
    const rowClassName = (data) => (isSelectable(data) ? "" : "p-disabled");

    // ── Search ────────────────────────────────────────────────
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({
            ...prev,
            global: { ...prev.global, value },
        }));
        setGlobalFilterValue(value);
    };

    // ── Table header: title left, search right ────────────────
    const tableHeader = (
        <div style={styles.tableHeader}>
            {/* Left: title block */}
            <div>
                <p style={styles.tableTitle}>Sites</p>
                <p style={styles.tableSub}>Click a site to view its measures</p>
            </div>

            {/* Right: search input */}
            <span className="p-input-icon-left">
                <i className="pi pi-search" style={styles.searchIcon} />
                <InputText
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder="Search sites…"
                    style={styles.searchInput}
                />
            </span>
        </div>
    );

    // ── Render ────────────────────────────────────────────────
    return (
        <div className="page-enter">

            {/* Stat cards */}
            <div style={styles.statsRow}>
                <StatCard icon="pi pi-building" label="Total Sites" value={totalSites.toLocaleString()} color="#4CAF50" tooltip="Total number of active sites registered under your account" />
                <StatCard icon="pi pi-list" label="Total Measures" value={totalMeasures.toLocaleString()} color="#3B82F6" tooltip="Combined total of all completed and invoiced measures across every site" />
            </div>

            {/* Data table */}
            <div style={styles.tableWrap}>
                <DataTable value={clientOverview} dataKey="systemId" selectionMode="single" selection={selectedSite} onSelectionChange={e => setSelectedSite(e.value)}
                    onRowSelect={onSiteRowSelect} filters={filters} globalFilterFields={["systemId", "siteName", "totalMeasures", "totalWorksheets"]}
                    header={tableHeader} paginator rows={10} rowsPerPageOptions={[10, 20, 50]} scrollable tableStyle={styles.table}
                    isDataSelectable={isRowSelectable} rowClassName={rowClassName}
                    emptyMessage="No sites available for the client." style={styles.dataTable} >
                    <Column field="systemId" header="System ID" hidden />
                    <Column field="siteName" header="Site Name" sortable style={styles.col} />
                    <Column field="siteRefNumber" header="SiteRefNumber" hidden />
                    <Column field="totalMeasures" header="Total Measures" sortable style={styles.col} />
                    <Column field="totalWorksheets" header="Total Worksheets" sortable style={styles.col} />
                </DataTable>
            </div>
        </div>
    );
};

// ── Styles ────────────────────────────────────────────────────
const styles = {
    /* Stat cards row */
    statsRow: {
        display: "flex",
        gap: 16,
        marginBottom: 24,
        flexWrap: "wrap",
    },

    /* Card wrapper for the DataTable */
    tableWrap: {
        background: "#ffffff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },

    /* DataTable root font */
    dataTable: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
    },

    /* Inner <table> element */
    table: {
        minWidth: "50rem",
    },

    /* Column cells */
    col: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        color: "#111827",
    },

    /* Header row: title left, search right */
    tableHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0",
        gap: 12,
        flexWrap: "wrap",
    },

    /* "Sites" heading */
    tableTitle: {
        fontFamily: "var(--font-sans)",
        fontSize: 16,
        fontWeight: 600,
        color: "#111827",
        margin: 0,
    },

    /* "Click a site…" sub-label */
    tableSub: {
        fontFamily: "var(--font-sans)",
        fontSize: 12,
        fontWeight: 400,
        color: "#9CA3AF",
        margin: "2px 0 0 0",
    },

    /* PrimeIcons search icon */
    searchIcon: {
        fontSize: 13,
        color: "#9CA3AF",
    },

    /* Search InputText */
    searchInput: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 36,
        paddingLeft: 32,
        borderRadius: 6,
        border: "1px solid #D1D5DB",
        width: 220,
        color: "#111827",
    },
};

export default Dashboard;
